import { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Legend, AreaChart, Area } from 'recharts';
import { Modal } from '@/components/ui/Modal';
import { Z_INDEX } from '@/constants/zIndex';
import { useMacroStore, type MacroDataPoint, MACRO_COLORS } from '@/store/macroStore';
import { useChartStore } from '@/store/chartStore';
import { api } from '@/services/api';
import { trackMacroDetailOpen, trackMacroDetailTab, trackMacroAI } from '@/services/gtag';
import { CCFPanel } from './CCFPanel';
import { ZScorePanel } from './ZScorePanel';
import { RegimeBreakBanner } from './RegimeBreakBanner';
import {
  detectRegimeBreak,
  aggregateToMonthly,
  computeHPCycleCorrelation,
  computeGrangerCausality,
  computeEWMACorrelation,
  computeRegimeCorrelation,
} from './macroMath';

interface MacroDetailModalProps {
  seriesId: string;
  onClose: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtMacroValue(value: number | undefined, unit: string | null | undefined): string {
  if (value === undefined || value === null || isNaN(value)) return '—';

  // Percentages: always 1 decimal place
  if (unit === '%') {
    return value.toFixed(1);
  }

  // Big-number formatting (K, Mn, Bn, Tr) for large values
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000_000_000) return sign + (abs / 1_000_000_000_000).toFixed(2) + 'Tr';
  if (abs >= 1_000_000_000)     return sign + (abs / 1_000_000_000).toFixed(2) + 'Bn';
  if (abs >= 1_000_000)         return sign + (abs / 1_000_000).toFixed(2) + 'Mn';
  if (abs >= 1_000)             return sign + (abs / 1_000).toFixed(1) + 'K';

  // Small numbers
  return abs < 1 ? value.toFixed(4) : value.toFixed(2);
}

function fmtDate(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function computeStats(data: MacroDataPoint[]) {
  if (!data || data.length === 0) return null;
  const values = data.map((d) => d.value);
  const latest = values[values.length - 1];
  const prev   = values.length >= 2 ? values[values.length - 2] : undefined;
  const mom    = prev !== undefined ? latest - prev : undefined;
  const momPct = prev !== undefined && prev !== 0 ? ((latest - prev) / Math.abs(prev)) * 100 : undefined;
  const max    = Math.max(...values);
  const min    = Math.min(...values);
  const avg    = values.reduce((s, v) => s + v, 0) / values.length;

  // YoY change (try to go back ~12 data points for monthly series)
  const yoyIdx = Math.max(0, values.length - 13);
  const yoyVal = values[yoyIdx];
  const yoy    = yoyVal !== undefined && values.length >= 2
    ? latest - yoyVal
    : undefined;

  return { latest, prev, mom, momPct, max, min, avg, yoy, unit: data[data.length - 1].unit };
}

function correlationColor(rho: number | null): string {
  if (rho === null) return 'text-muted';
  const abs = Math.abs(rho);
  if (abs >= 0.5) return 'text-emerald-400';
  if (abs >= 0.3) return 'text-yellow-400';
  return 'text-muted';
}

function correlationLabel(rho: number | null): string {
  if (rho === null) return 'Insufficient data';
  const abs = Math.abs(rho);
  const dir = rho >= 0 ? 'positive' : 'negative';
  if (abs >= 0.7) return `Strong ${dir} correlation`;
  if (abs >= 0.5) return `Moderate ${dir} correlation`;
  if (abs >= 0.3) return `Weak ${dir} correlation`;
  return 'Very weak / no correlation';
}

// ── Markdown renderer ─────────────────────────────────────────────────────────

/** Renders inline text, converting **bold** to <strong>. */
function renderInline(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={i} className="font-semibold text-text">{part.replace(/\*\*/g, '')}</strong>
      : part,
  );
}

/**
 * Splits AI text on **Section Title**: boundaries and renders each section
 * as a card with an accent label and body text.
 */
function renderMarkdown(text: string): React.ReactNode {
  // Split on **Title**: boundaries (keep the delimiter via capturing group)
  const parts = text.split(/(\*\*[^*]+\*\*:)/g);

  if (parts.length <= 1) {
    // No sections — plain paragraph(s)
    return (
      <p className="text-xs text-text leading-relaxed">{renderInline(text)}</p>
    );
  }

  const sections: { title: string; body: string }[] = [];
  let i = 0;
  if (parts[0].trim()) sections.push({ title: '', body: parts[0].trim() });
  i = 1;
  while (i < parts.length) {
    const titleRaw = parts[i] ?? '';
    const body = (parts[i + 1] ?? '').trim();
    const title = titleRaw.replace(/^\*\*/, '').replace(/\*\*:$/, '');
    if (title || body) sections.push({ title, body });
    i += 2;
  }

  return (
    <div className="space-y-2">
      {sections.map(({ title, body }, idx) => (
        <div key={idx} className="bg-panel-bg border border-border rounded-lg px-3 py-2.5">
          {title && (
            <p className="text-[10px] font-semibold text-accent uppercase tracking-wider mb-1">{title}</p>
          )}
          <p className="text-xs text-text leading-relaxed">{renderInline(body)}</p>
        </div>
      ))}
    </div>
  );
}

// Downsample to at most N points for chart perf
function downsample(data: MacroDataPoint[], maxPoints = 120): MacroDataPoint[] {
  if (data.length <= maxPoints) return data;
  const step = Math.ceil(data.length / maxPoints);
  const result: MacroDataPoint[] = [];
  for (let i = 0; i < data.length; i += step) result.push(data[i]);
  if (result[result.length - 1] !== data[data.length - 1]) result.push(data[data.length - 1]);
  return result;
}

/** Merge multiple series into a single timeline for Recharts. */
function mergeSeriesData(
  datasets: Record<string, MacroDataPoint[]>,
): Array<Record<string, number | string | null>> {
  const dates = new Set<string>();
  for (const pts of Object.values(datasets)) {
    for (const p of pts) dates.add(p.date);
  }
  const sorted = Array.from(dates).sort();
  return sorted.map((date) => {
    const row: Record<string, number | string | null> = { date };
    for (const [key, pts] of Object.entries(datasets)) {
      const pt = pts.find((p) => p.date === date);
      row[key] = pt?.value ?? null;
    }
    return row;
  });
}

// ── Chart tooltip ─────────────────────────────────────────────────────────────

interface TooltipPayload {
  value?: number;
  payload?: { date?: string; unit?: string | null };
}

function CustomTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayload[] }) {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  const unit = item.payload?.unit ?? '';
  return (
    <div className="bg-panel border border-border rounded-lg px-2.5 py-1.5 text-xs shadow-lg">
      <p className="text-muted">{item.payload?.date}</p>
      <p className="text-text font-medium">{fmtMacroValue(item.value, unit)}{unit ? ` ${unit}` : ''}</p>
    </div>
  );
}

interface BreakdownTooltipPayloadItem {
  dataKey?: string;
  value?: number;
  name?: string;
  color?: string;
  payload?: { date?: string };
}

function BreakdownTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: BreakdownTooltipPayloadItem[];
}) {
  if (!active || !payload || !payload.length) return null;

  const items = payload
    .map((p) => ({
      name: p.name ?? p.dataKey ?? '',
      value: p.value,
      color: p.color ?? '#3b82f6',
    }))
    .filter((p): p is typeof p & { value: number } => p.value !== undefined && p.value !== null && !isNaN(p.value));

  const total = items.reduce((sum, p) => sum + p.value, 0);

  return (
    <div className="bg-panel border border-border rounded-lg px-2.5 py-1.5 text-xs shadow-lg min-w-[140px]">
      <p className="text-muted mb-1">{payload[0]?.payload?.date}</p>
      {items.map((item) => {
        const pct = total !== 0 ? (item.value / total) * 100 : 0;
        return (
          <div key={item.name} className="flex items-center justify-between gap-3 py-0.5">
            <span className="font-medium truncate" style={{ color: item.color }}>
              {item.name}
            </span>
            <span className="text-text tabular-nums shrink-0">
              {fmtMacroValue(item.value, null)} ({pct.toFixed(1)}%)
            </span>
          </div>
        );
      })}
      {total !== 0 && (
        <div className="flex items-center justify-between gap-3 pt-1 mt-1 border-t border-border">
          <span className="font-medium text-muted">Total</span>
          <span className="text-text font-semibold tabular-nums shrink-0">
            {fmtMacroValue(total, null)}
          </span>
        </div>
      )}
    </div>
  );
}

function RelatedTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: BreakdownTooltipPayloadItem[];
}) {
  if (!active || !payload || !payload.length) return null;

  const items = payload
    .map((p) => ({
      name: p.name ?? p.dataKey ?? '',
      value: p.value,
      color: p.color ?? '#3b82f6',
    }))
    .filter((p): p is typeof p & { value: number } => p.value !== undefined && p.value !== null && !isNaN(p.value));

  return (
    <div className="bg-panel border border-border rounded-lg px-2.5 py-1.5 text-xs shadow-lg min-w-[140px]">
      <p className="text-muted mb-1">{payload[0]?.payload?.date}</p>
      {items.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-3 py-0.5">
          <span className="font-medium truncate" style={{ color: item.color }}>
            {item.name}
          </span>
          <span className="text-text tabular-nums shrink-0">
            {fmtMacroValue(item.value, null)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function MacroDetailModal({ seriesId, onClose }: MacroDetailModalProps) {
  const seriesData = useMacroStore((s) => s.seriesData[seriesId]);
  const seriesMeta = useMacroStore((s) => s.seriesMeta.find((m) => m.series_id === seriesId));
  const pinned     = useMacroStore((s) => s.pinnedSeries.find((p) => p.seriesId === seriesId));
  const rho        = useMacroStore((s) => s.correlations[seriesId] ?? null);

  const symbol    = useChartStore((s) => s.symbol);
  const timeframe = useChartStore((s) => s.timeframe);

  const [activeTab, setActiveTab] = useState<'trend' | 'ccf' | 'zscore' | 'breakdown' | 'related'>('trend');

  // Track detail modal open on mount
  useEffect(() => {
    trackMacroDetailOpen(seriesId, 'trend');
  }, [seriesId]);
  const [selectedLag, setSelectedLag] = useState<number | null>(null);

  // ── Relationship data ───────────────────────────────────────────────────────
  const allSeries = useMacroStore((s) => s.seriesMeta);
  const children = useMemo(
    () => allSeries.filter((s) => s.parent_series_id === seriesId),
    [allSeries, seriesId],
  );
  const peers = useMemo(
    () => (seriesMeta?.related_series ?? [])
      .map((id) => allSeries.find((s) => s.series_id === id))
      .filter(Boolean) as import('@/store/macroStore').MacroSeries[],
    [allSeries, seriesMeta],
  );

  const [childData, setChildData] = useState<Record<string, MacroDataPoint[]>>({});
  const [peerData, setPeerData] = useState<Record<string, MacroDataPoint[]>>({});

  useEffect(() => {
    if (activeTab !== 'breakdown' || children.length === 0) return;
    const ids = children.map((c) => c.series_id);
    api.macroData(ids)
      .then((res) => setChildData(res.data))
      .catch(console.error);
  }, [activeTab, children]);

  useEffect(() => {
    if (activeTab !== 'related' || peers.length === 0) return;
    const ids = [seriesId, ...peers.map((p) => p.series_id)];
    api.macroData(ids)
      .then((res) => setPeerData(res.data))
      .catch(console.error);
  }, [activeTab, peers, seriesId]);

  const breakdownData = useMemo(() => mergeSeriesData(childData), [childData]);
  const relatedData = useMemo(() => mergeSeriesData(peerData), [peerData]);

  const [aiText,    setAiText]    = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError,   setAiError]   = useState('');

  const stats   = useMemo(() => computeStats(seriesData ?? []), [seriesData]);
  const samples = useMemo(() => downsample(seriesData ?? []), [seriesData]);
  const color   = pinned?.color ?? '#3b82f6';

  // ── Price bars for quantitative analysis ──────────────────────────────────
  const [priceBars, setPriceBars] = useState<Array<{ time: number; close: number }>>([]);
  useEffect(() => {
    if (!symbol) return;
    api
      .ohlcv(symbol, timeframe, 500)
      .then((res) => setPriceBars(res.data.map((b) => ({ time: b.time, close: b.close }))))
      .catch(console.error);
  }, [symbol, timeframe]);

  const monthlyPrices = useMemo(() => aggregateToMonthly(priceBars), [priceBars]);

  const grangerResult = useMemo(
    () => computeGrangerCausality(seriesData ?? [], monthlyPrices, 3),
    [seriesData, monthlyPrices],
  );
  const hpCycleR = useMemo(
    () => computeHPCycleCorrelation(seriesData ?? [], monthlyPrices),
    [seriesData, monthlyPrices],
  );
  const ewmaResult = useMemo(
    () => computeEWMACorrelation(seriesData ?? [], monthlyPrices),
    [seriesData, monthlyPrices],
  );
  const regimeResult = useMemo(
    () => computeRegimeCorrelation(seriesData ?? [], monthlyPrices),
    [seriesData, monthlyPrices],
  );

  const chartData = useMemo(
    () => samples.map((d) => ({ date: fmtDate(d.date), value: d.value, unit: d.unit })),
    [samples],
  );

  const avgLine = stats?.avg;

  // Regime break detection (used for banner)
  const regimeBreak = useMemo(() => {
    if (!seriesData || seriesData.length < 12) return { isBreak: false, message: '' };
    // We don't have price bars here; approximate using correlation as proxy for recent peak
    return detectRegimeBreak([], seriesData, 12, 0.3);
  }, [seriesData]);

  async function handleAiInterpretation() {
    if (aiLoading) return;
    trackMacroAI(seriesId, symbol);
    setAiLoading(true);
    setAiError('');
    setAiText('');

    const context = [
      `Macro series: ${seriesMeta?.name ?? seriesId}`,
      `Category: ${seriesMeta?.category ?? '—'}`,
      `Frequency: ${seriesMeta?.frequency ?? '—'}`,
      seriesMeta?.description ? `Description: ${seriesMeta.description}` : '',
      stats ? [
        `Latest value: ${fmtMacroValue(stats.latest, stats.unit)} ${stats.unit ?? ''}`,
        stats.mom !== undefined ? `MoM change: ${stats.mom >= 0 ? '+' : ''}${fmtMacroValue(stats.mom, stats.unit)} ${stats.unit ?? ''}` : '',
        stats.yoy !== undefined ? `YoY change: ${stats.yoy >= 0 ? '+' : ''}${fmtMacroValue(stats.yoy, stats.unit)} ${stats.unit ?? ''}` : '',
        `52-period range: ${fmtMacroValue(stats.min, stats.unit)} – ${fmtMacroValue(stats.max, stats.unit)} ${stats.unit ?? ''}`,
        `Long-term average: ${fmtMacroValue(stats.avg, stats.unit)} ${stats.unit ?? ''}`,
      ].filter(Boolean).join('\n') : '',
      rho !== null ? `Pearson correlation with stock price: ${rho.toFixed(3)} (${correlationLabel(rho)})` : '',
    ].filter(Boolean).join('\n');

    try {
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `Provide a concise, 3–4 sentence interpretation of the following Sri Lankan macroeconomic indicator data in the context of stock market analysis. Focus on what the latest reading implies for equities.\n\n${context}`,
          symbol,
          timeframe,
        }),
      });
      if (!res.ok) {
        const j = (await res.json()) as { error?: string };
        setAiError(j.error ?? 'AI request failed');
        return;
      }
      const json = (await res.json()) as { text?: string; error?: string };
      if (json.error) { setAiError(json.error); return; }
      setAiText(json.text ?? '');
    } catch {
      setAiError('Failed to get AI interpretation. Please try again.');
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <Modal size="xl" align="center" onClose={onClose} zIndex={Z_INDEX.modalStacked} className="max-h-[90vh]">
      <Modal.Header
        title={
          <span className="flex items-center gap-2">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
              style={{ background: color }}
            />
            {seriesMeta?.name ?? seriesId}
          </span>
        }
        onClose={onClose}
      />
      <Modal.Body className="gap-4 max-h-[85vh] overflow-y-auto">

        {/* ── Stats grid ─────────────────────────────────────────────────── */}
        {stats ? (
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Latest', value: `${fmtMacroValue(stats.latest, stats.unit)} ${stats.unit ?? ''}` },
              { label: 'MoM Change', value: stats.mom !== undefined ? `${stats.mom >= 0 ? '+' : ''}${fmtMacroValue(stats.mom, stats.unit)}` : '—', color: stats.mom !== undefined ? (stats.mom >= 0 ? 'text-emerald-400' : 'text-red-400') : '' },
              { label: 'YoY Change', value: stats.yoy !== undefined ? `${stats.yoy >= 0 ? '+' : ''}${fmtMacroValue(stats.yoy, stats.unit)}` : '—', color: stats.yoy !== undefined ? (stats.yoy >= 0 ? 'text-emerald-400' : 'text-red-400') : '' },
              { label: 'Period High', value: fmtMacroValue(stats.max, stats.unit) },
              { label: 'Period Low',  value: fmtMacroValue(stats.min, stats.unit) },
              { label: 'LT Average',  value: fmtMacroValue(stats.avg, stats.unit) },
            ].map(({ label, value, color: valColor }) => (
              <div key={label} className="bg-panel-bg rounded-lg px-3 py-2 border border-border">
                <p className="text-[10px] text-muted mb-0.5">{label}</p>
                <p className={`text-sm font-semibold ${valColor ?? 'text-text'}`}>{value}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted text-center py-4">No data loaded yet.</p>
        )}

        {/* ── Regime break banner ────────────────────────────────────────── */}
        <RegimeBreakBanner message={regimeBreak.message} seriesId={seriesId} />

        {/* ── Tabs ───────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-1 border-b border-border pb-0">
          {([
            { key: 'trend', label: 'Historical Trend' },
            { key: 'ccf', label: 'CCF' },
            { key: 'zscore', label: 'Z-Score' },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                if (activeTab !== tab.key) {
                  trackMacroDetailTab(seriesId, tab.key);
                }
                setActiveTab(tab.key);
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors relative ${
                activeTab === tab.key
                  ? 'text-accent'
                  : 'text-muted hover:text-text'
              }`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-accent rounded-full" />
              )}
            </button>
          ))}
          {children.length > 0 && (
            <button
              onClick={() => {
                if (activeTab !== 'breakdown') {
                  trackMacroDetailTab(seriesId, 'breakdown');
                }
                setActiveTab('breakdown');
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors relative ${
                activeTab === 'breakdown'
                  ? 'text-accent'
                  : 'text-muted hover:text-text'
              }`}
            >
              Breakdown
              {activeTab === 'breakdown' && (
                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-accent rounded-full" />
              )}
            </button>
          )}
          {peers.length > 0 && (
            <button
              onClick={() => {
                if (activeTab !== 'related') {
                  trackMacroDetailTab(seriesId, 'related');
                }
                setActiveTab('related');
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors relative ${
                activeTab === 'related'
                  ? 'text-accent'
                  : 'text-muted hover:text-text'
              }`}
            >
              Related
              {activeTab === 'related' && (
                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-accent rounded-full" />
              )}
            </button>
          )}
        </div>

        {/* ── Tab content ────────────────────────────────────────────────── */}
        <div className="min-h-[340px]">
          {activeTab === 'trend' && chartData.length > 0 && (
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                    tickFormatter={(v: number) => fmtMacroValue(v, chartData[0]?.unit ?? null)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {avgLine !== undefined && (
                    <ReferenceLine y={avgLine} stroke="var(--color-muted)" strokeDasharray="4 2" strokeWidth={1} />
                  )}
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={color}
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 3, fill: color, stroke: 'none' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeTab === 'trend' && chartData.length === 0 && (
            <div className="h-[340px] flex items-center justify-center text-xs text-muted">
              No data loaded yet.
            </div>
          )}

          {activeTab === 'ccf' && (
            <div className="h-[340px]">
              <CCFPanel
                seriesId={seriesId}
                color={color}
                onLagSelect={setSelectedLag}
              />
            </div>
          )}

          {activeTab === 'zscore' && (
            <div className="h-[340px]">
              <ZScorePanel
                seriesId={seriesId}
                color={color}
                highlightedLag={selectedLag}
                pearsonR={rho}
              />
            </div>
          )}

          {activeTab === 'breakdown' && breakdownData.length > 0 && (
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={breakdownData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <defs>
                    {children.map((child, i) => {
                      const c = MACRO_COLORS[i % MACRO_COLORS.length];
                      return (
                        <linearGradient key={child.series_id} id={`grad-${child.series_id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={c} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={c} stopOpacity={0} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip content={<BreakdownTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10, color: 'var(--color-muted)' }} />
                  {children.map((child, i) => {
                    const c = MACRO_COLORS[i % MACRO_COLORS.length];
                    return (
                      <Area
                        key={child.series_id}
                        type="monotone"
                        dataKey={child.series_id}
                        name={child.name}
                        stroke={c}
                        strokeWidth={1.5}
                        fill={`url(#grad-${child.series_id})`}
                        dot={false}
                        connectNulls
                      />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeTab === 'breakdown' && breakdownData.length === 0 && (
            <div className="h-[340px] flex items-center justify-center text-xs text-muted">
              {children.length === 0 ? 'No breakdown available.' : 'Loading breakdown data…'}
            </div>
          )}

          {activeTab === 'related' && relatedData.length > 0 && (
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={relatedData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                    label={{
                      value: seriesData?.[0]?.unit ?? '',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: 10, fill: 'var(--color-muted)' },
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                    label={{
                      value: (() => {
                        const units = new Set(peers.map((p) => peerData[p.series_id]?.[0]?.unit).filter(Boolean));
                        return units.size === 1 ? Array.from(units)[0] : '';
                      })(),
                      angle: 90,
                      position: 'insideRight',
                      style: { fontSize: 10, fill: 'var(--color-muted)' },
                    }}
                  />
                  <Tooltip content={<RelatedTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10, color: 'var(--color-muted)' }} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey={seriesId}
                    name={seriesMeta?.name ?? seriesId}
                    stroke={color}
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls
                  />
                  {peers.map((peer, i) => {
                    const pool = MACRO_COLORS.filter((c) => c !== color);
                    const peerColor = pool[i % pool.length];
                    return (
                      <Line
                        key={peer.series_id}
                        yAxisId="right"
                        type="monotone"
                        dataKey={peer.series_id}
                        name={peer.name}
                        stroke={peerColor}
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls
                      />
                    );
                  })}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeTab === 'related' && relatedData.length === 0 && (
            <div className="h-[340px] flex items-center justify-center text-xs text-muted">
              {peers.length === 0 ? 'No related series available.' : 'Loading related data…'}
            </div>
          )}
        </div>

        {/* ── Correlation analysis cards ────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-2">

          {/* #1 Pearson ρ */}
          <div className="bg-panel-bg border border-border rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-medium text-muted uppercase tracking-wider mb-1.5">
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded bg-accent/15 text-accent font-bold mr-1.5" style={{ fontSize: '8px' }}>1</span>
              Pearson ρ
            </p>
            {rho !== null ? (
              <>
                <p className={`text-xl font-bold tabular-nums ${correlationColor(rho)}`}>
                  {rho >= 0 ? '+' : ''}{rho.toFixed(3)}
                </p>
                <p className={`text-[10px] mt-0.5 ${correlationColor(rho)}`}>{correlationLabel(rho)}</p>
              </>
            ) : (
              <p className="text-sm font-bold text-muted">—</p>
            )}
          </div>

          {/* #2 Granger Causality */}
          <div className="bg-panel-bg border border-border rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-medium text-muted uppercase tracking-wider mb-1.5">
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded bg-accent/15 text-accent font-bold mr-1.5" style={{ fontSize: '8px' }}>2</span>
              Granger Causality
            </p>
            {grangerResult.sufficientData ? (
              <>
                <p className={`text-xl font-bold tabular-nums ${grangerResult.causalityDetected ? 'text-emerald-400' : 'text-muted'}`}>
                  p = {grangerResult.pValue?.toFixed(3) ?? '—'}
                </p>
                <p className={`text-[10px] mt-0.5 ${grangerResult.causalityDetected ? 'text-emerald-400' : 'text-muted'}`}>
                  {grangerResult.causalityDetected ? `Causality confirmed · F=${grangerResult.fStat?.toFixed(1)}` : 'No significant causality'}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-bold text-muted">—</p>
                <p className="text-[10px] mt-0.5 text-muted">Needs 22+ aligned months</p>
              </>
            )}
          </div>

          {/* #3 HP Cycle Correlation */}
          <div className="bg-panel-bg border border-border rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-medium text-muted uppercase tracking-wider mb-1.5">
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded bg-accent/15 text-accent font-bold mr-1.5" style={{ fontSize: '8px' }}>3</span>
              HP Cycle <span className="normal-case font-normal">(λ=129k)</span>
            </p>
            {hpCycleR !== null ? (
              <>
                <p className={`text-xl font-bold tabular-nums ${correlationColor(hpCycleR)}`}>
                  {hpCycleR >= 0 ? '+' : ''}{hpCycleR.toFixed(3)}
                </p>
                <p className={`text-[10px] mt-0.5 ${correlationColor(hpCycleR)}`}>
                  {correlationLabel(hpCycleR)} · trend-adjusted
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-bold text-muted">—</p>
                <p className="text-[10px] mt-0.5 text-muted">Needs 12+ aligned months</p>
              </>
            )}
          </div>

          {/* #4 DCC-GARCH (EWMA proxy) */}
          <div className="bg-panel-bg border border-border rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-medium text-muted uppercase tracking-wider mb-1.5">
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded bg-accent/15 text-accent font-bold mr-1.5" style={{ fontSize: '8px' }}>4</span>
              DCC-GARCH <span className="normal-case font-normal">(EWMA λ=0.94)</span>
            </p>
            {ewmaResult.sufficientData && ewmaResult.currentCorr !== null ? (
              <>
                <p className={`text-xl font-bold tabular-nums ${correlationColor(ewmaResult.currentCorr)}`}>
                  {ewmaResult.currentCorr >= 0 ? '+' : ''}{ewmaResult.currentCorr.toFixed(3)}
                </p>
                <p className={`text-[10px] mt-0.5 ${correlationColor(ewmaResult.currentCorr)}`}>
                  Dynamic corr. {ewmaResult.trend === 'strengthening' ? '↗ strengthening' : ewmaResult.trend === 'weakening' ? '↘ weakening' : '→ stable'}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-bold text-muted">—</p>
                <p className="text-[10px] mt-0.5 text-muted">Needs 24+ aligned months</p>
              </>
            )}
          </div>

          {/* #5 HMM Regime (full width) */}
          <div className="col-span-2 bg-panel-bg border border-border rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-medium text-muted uppercase tracking-wider mb-1.5">
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded bg-accent/15 text-accent font-bold mr-1.5" style={{ fontSize: '8px' }}>5</span>
              HMM Regime <span className="normal-case font-normal">(2-state median split)</span>
            </p>
            {regimeResult.sufficientData ? (
              <div className="flex items-center gap-4">
                <div>
                  <span className={`text-sm font-bold ${regimeResult.currentRegime === 'high' ? 'text-emerald-400' : 'text-yellow-400'}`}>
                    Currently: {regimeResult.currentRegime === 'high' ? 'High' : 'Low'} regime
                  </span>
                </div>
                <div className="flex gap-4 text-[10px] text-muted">
                  <span>High: <span className="text-text font-medium">{regimeResult.highRegime.label}</span></span>
                  <span>Low: <span className="text-text font-medium">{regimeResult.lowRegime.label}</span></span>
                </div>
              </div>
            ) : (
              <p className="text-[10px] text-muted">Needs 36+ aligned months (3+ years of overlap)</p>
            )}
          </div>

        </div>

        {/* ── AI interpretation ──────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-medium text-muted uppercase tracking-wider">AI Interpretation</p>
            <button
              onClick={handleAiInterpretation}
              disabled={aiLoading || !stats}
              className="px-3 py-1 text-xs bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors"
            >
              {aiLoading ? 'Thinking…' : aiText ? 'Refresh' : 'Get Interpretation'}
            </button>
          </div>

          {aiError && (
            <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">{aiError}</p>
          )}

          {aiText ? (
            renderMarkdown(aiText)
          ) : !aiLoading && (
            <p className="text-sm text-muted italic">
              Click &quot;Get Interpretation&quot; for an AI analysis of this indicator.
            </p>
          )}

          {aiLoading && (
            <div className="flex items-center gap-2 text-sm text-muted">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Analysing indicator…
            </div>
          )}
        </div>

        {/* ── Meta ──────────────────────────────────────────────────────── */}
        {seriesMeta?.description && (
          <p className="text-[11px] text-muted leading-relaxed border-t border-border pt-3">
            {seriesMeta.description}
          </p>
        )}
      </Modal.Body>
    </Modal>
  );
}
